import { supabase } from './supabase';
import { SUBSCRIPTION_TIERS } from '../types/subscriptions';
import { logger } from '../utils/logger';
import { getCachedProjectAccess, cacheProjectAccess } from './offline-db';

// Cache for permissions to avoid repeated API calls
let permissionsCache: Record<string, unknown> | null = null;
let permissionsCacheExpiry = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Lock map to prevent race conditions during concurrent access checks
const accessCheckLocks = new Map<string, Promise<boolean>>();

/**
 * Fetches user permissions from the server, with caching
 * @returns User permissions including VIP status and subscription tier
 */
async function getUserPermissions() {
  const now = Date.now();

  // Return cached permissions if valid
  if (permissionsCache && permissionsCacheExpiry > now) {
    return permissionsCache;
  }

  try {
    logger.fetch('Fetching user permissions from server');
    const { data, error } = await supabase.functions.invoke('get-user-permissions');

    if (error) {
      logger.error('Permissions', 'Error fetching permissions:', error);
      throw error;
    }

    // Cache the permissions
    permissionsCache = data;
    permissionsCacheExpiry = now + CACHE_TTL;

    return data;
  } catch (err) {
    logger.error('Permissions', 'Error fetching permissions:', err);
    throw err;
  }
}

/**
 * Enforces project creation limits based on user's permissions
 *
 * IMPORTANT: VIP Status is now checked server-side by the get-user-permissions Edge Function
 * 
 * Rules:
 * - VIP users have unlimited access to all features regardless of subscription plan
 * - Free users are limited to 3 projects
 * - Pro users and team plans have unlimited projects
 *
 * @param userId The user ID to check limits for
 * @returns Object containing whether project creation is allowed and remaining project count
 */
export async function enforceProjectCreationLimit(userId: string) {
  try {
    // Get user data to check if this is a new user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError) throw userError;

    // New users might have incomplete profiles, treat them specially
    const isNewUser = !user?.app_metadata?.provider;

    // For brand new users, allow project creation as a fallback
    if (isNewUser) {
      return {
        allowed: true,
        remainingProjects: 3,
      };
    }

    // Get permissions from server
    const permissions = await getUserPermissions();
    const isVIP = permissions.isVIP;
    
    // VIP users have unlimited projects
    if (isVIP) {
      return {
        allowed: true,
        remainingProjects: 'unlimited',
      };
    }

    // Get current project count
    const { count, error: countError } = await supabase
      .from('projects')
      .select('*', { count: 'exact' })
      .eq('user_id', userId);

    if (countError) {
      throw new Error('Failed to count projects');
    }

    // Get max projects from permissions
    const maxProjects = permissions.permissions.maxProjects;

    // Check if user has reached their limit
    if (maxProjects !== 'unlimited' && count >= maxProjects) {
      return {
        allowed: false,
        message: `You've reached the maximum of ${maxProjects} projects for your current plan. Upgrade to create more projects.`,
        currentCount: count,
        maxProjects,
      };
    }

    return {
      allowed: true,
      remainingProjects: maxProjects === 'unlimited' ? 'unlimited' : maxProjects - count,
      currentCount: count,
      maxProjects,
    };
  } catch (error) {
    logger.error('Error checking project limits:', error);
    
    // Default to allowing one project on error for new users
    return {
      allowed: true,
      remainingProjects: 1,
      error: 'Failed to check project limits'
    };
  }
}

/**
 * Checks if the user can access a specific project based on subscription rules
 *
 * Rules for access:
 * - VIP users can access all projects
 * - Users can always access projects shared with them (where they're not the owner)
 * - Paid tier users can access all their projects
 * - Free tier users can only access their first 3 projects (by creation date)
 *
 * @param userId User ID to check
 * @param projectId Project ID to check access to
 * @returns Promise resolving to a boolean indicating if access is allowed
 */
export async function canAccessProject(userId: string, projectId: string): Promise<boolean> {
  // Create a unique lock key for this user-project combination
  const lockKey = `${userId}_${projectId}`;
  
  // Check if there's already an ongoing check for this combination
  const existingCheck = accessCheckLocks.get(lockKey);
  if (existingCheck) {
    logger.connectivity('Reusing existing access check', { userId, projectId });
    return existingCheck;
  }
  
  // Create a new check promise with cleanup
  const checkPromise = performAccessCheck(userId, projectId)
    .finally(() => {
      // Clean up the lock after a short delay to ensure all concurrent callers get the result
      setTimeout(() => {
        accessCheckLocks.delete(lockKey);
      }, 100); // 100ms delay to handle concurrent calls
    });
  
  // Store it in the lock map
  accessCheckLocks.set(lockKey, checkPromise);
  
  // Return the promise directly
  return checkPromise;
}

/**
 * Internal function that performs the actual access check
 * This is separated to enable the locking mechanism
 */
async function performAccessCheck(userId: string, projectId: string): Promise<boolean> {
  try {
    // Check if we're offline
    const isOffline = !navigator.onLine;
    
    // If offline, try to use cached access
    if (isOffline) {
      const cachedAccess = await getCachedProjectAccess(userId, projectId);
      if (cachedAccess !== null) {
        logger.connectivity('Using cached project access while offline', { projectId, allowed: cachedAccess });
        return cachedAccess;
      }
      // If no cache available while offline, deny access
      logger.warn('No cached project access available while offline', { projectId });
      return false;
    }
    
    // Run ownership and sharing checks in parallel
    const [
      ownedResult,
      sharedResult
    ] = await Promise.all([
      // Check if user is the owner of this project
      supabase
        .from('projects')
        .select('id, created_at')
        .eq('user_id', userId)
        .eq('id', projectId)
        .single(),

      // Check if project is shared with the user
      supabase
        .from('project_collaborators')
        .select('id') // Use 'id' instead of 'project_id' to avoid URL conflicts
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .limit(1),
    ]);

    const ownedProject = ownedResult.data;
    const sharedProject = sharedResult.data && sharedResult.data.length > 0 ? sharedResult.data[0] : null;

    // If user doesn't own the project and it's not shared with them, deny access
    if (!ownedProject && !sharedProject) {
      // Cache the denial
      await cacheProjectAccess(userId, projectId, false);
      return false;
    }

    // If the project is shared with the user (but they don't own it), always allow access
    if (sharedProject && !ownedProject) {
      // Cache the allowed access
      await cacheProjectAccess(userId, projectId, true);
      return true;
    }

    // At this point, we know the user owns the project
    // Get permissions from server
    const permissions = await getUserPermissions();
    const isVIP = permissions.isVIP;

    // VIP users have unlimited access
    if (isVIP) {
      // Cache the allowed access
      await cacheProjectAccess(userId, projectId, true);
      return true;
    }

    // Check if user can access all their projects
    if (permissions.permissions.canAccessAllProjects) {
      // Cache the allowed access
      await cacheProjectAccess(userId, projectId, true);
      return true;
    }

    // For free tier users, check if this is among their first N projects
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('id, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });
      
    if (projectsError || !projects) {
      logger.error('Error fetching projects for access check:', projectsError);
      return false;
    }

    // Get max projects from subscription tier
    const maxProjects = SUBSCRIPTION_TIERS.free.maxProjects as number;

    // Check if this project is among the first N allowed projects
    const allowedProjects = projects.slice(0, maxProjects);
    const isAllowedProject = allowedProjects.some(p => p.id === projectId);

    // Cache the access result
    await cacheProjectAccess(userId, projectId, isAllowedProject);
    
    return isAllowedProject;
  } catch (error) {
    logger.error('Error checking project access permissions:', error);
    return false; // Default to denying access on error
  }
}

/**
 * Checks if a user has exceeded their project limit based on subscription
 * Used to display warnings without restricting access to the project list
 *
 * @param userId User ID to check
 * @returns Object containing whether limit is exceeded and count information
 */
export async function hasExceededProjectLimit(userId: string): Promise<{
  limitExceeded: boolean;
  projectCount: number;
  allowedProjects: number | 'unlimited';
}> {
  try {
    // Get project count and permissions in parallel
    const [
      projectCountResult,
      permissions
    ] = await Promise.all([
      // Get user's project count
      supabase.from('projects').select('*', { count: 'exact' }).eq('user_id', userId),
      
      // Get user permissions
      getUserPermissions()
    ]);

    if (projectCountResult.error) throw projectCountResult.error;

    const projectCount = projectCountResult.count || 0;
    const isVIP = permissions.isVIP;
    
    // VIP users have unlimited access
    if (isVIP) {
      return {
        limitExceeded: false,
        projectCount,
        allowedProjects: 'unlimited',
      };
    }

    // Get max projects from permissions
    const allowedProjects = permissions.permissions.maxProjects;

    // Check if limit is exceeded
    const limitExceeded = allowedProjects !== 'unlimited' && projectCount > allowedProjects;

    return {
      limitExceeded,
      projectCount,
      allowedProjects,
    };
  } catch (error) {
    logger.error('Error checking project limit status:', error);
    // Default to showing no warning on error
    return {
      limitExceeded: false,
      projectCount: 0,
      allowedProjects: 0,
    };
  }
}